import { UploadFileStatus } from './../stores/agent';
import {
  abortMultipartUpload,
  completeMultipartUpload,
  createMultipartUpload,
  uploadFile,
  uploadPart,
} from '@/services/chat';
import agentStore from '@/stores/agent';
import Axios, { Canceler } from 'axios';
import { EventEmitter } from 'events';
import { useState, useEffect, useCallback } from 'react';

enum TaskStatus {
  pending, // 还未上传
  uploading, // 上传中
  done, // 已上传
}

interface Task {
  index: number; // 分片的顺序下标
  chunk: Blob; // 分片的 Blob
  startPos: number; // 分片在整个文件中的 offset
  endPos: number; // 分片在整个文件中的 offset + chunkSize
  status?: TaskStatus; // 分片的上传状态
  cancel?: Canceler; // 用于中断当前的上传请求
  loaded: number; // 该分片的已上传的文件大小
  errorCount: number;
}

export class UploadManager extends EventEmitter {
  quiet: boolean;
  file: File;
  chunkNum!: number;
  windowSize!: number;
  upload_id!: string;
  file_key!: string;
  isAbort = false;
  taskList: Task[];
  runningTask: Task[] = [];
  chunkSize: number;
  hasBeenSentCount: number;
  completed_parts: {
    etag: string;
    part_num: string;
  }[] = [];
  fileSize: number;
  shouldMultipartUpload: boolean;
  runningCount = 0;
  constructor(
    file: File,
    option?: {
      quiet?: boolean;
      multipartUploadBlockSize?: number;
      multipartUploadConcurrency?: number;
      chunkSize?: number;
    },
  ) {
    super();
    // multipartUploadConcurrency: 并发数
    // multipartUploadBlockSize: 文件大小限制
    const { quiet, multipartUploadConcurrency, multipartUploadBlockSize, chunkSize } = option || {};

    this.quiet = quiet || true;
    this.file = file;
    this.windowSize = multipartUploadConcurrency || 3;
    this.taskList = [];
    this.chunkSize = chunkSize || 5 * 1024 * 1024; // 单个分片大小需大于 5 MB,小于 10 MB
    this.fileSize = this.file.size;
    this.chunkNum = Math.ceil(this.fileSize / this.chunkSize); // 分片数 = 向上取整(总的文件大小/单个分片大小)
    this.hasBeenSentCount = 0;
    this.shouldMultipartUpload = this.fileSize >= (multipartUploadBlockSize || 10 * 1024 * 1024);

    setTimeout(() => {
      this.init();
    }, 0);
  }

  async init() {
    if (!this.shouldMultipartUpload) {
      this.singleUpload();
      return;
    }
    const res = await createMultipartUpload(this.file.name, this.fileSize);
    if (res instanceof Error) {
      this.onError(res);
      return;
    }

    const { data: { upload_id, file_key } = {}} = res;

    if (!upload_id || !file_key) {
      return;
    }
    this.upload_id = upload_id;
    this.file_key = file_key;
    this.emit('init', {
      file: this.file,
      size: this.fileSize,
      chunkSize: this.chunkSize,
      chunkNum: this.chunkNum,
      windowSize: this.windowSize,
      upload_id,
      file_key,
    });
    this.upload();
  }

  async singleUpload() {
    this.emit('init', {
      file: this.file,
      size: this.fileSize,
    });
    const res = await uploadFile(this.file, {
      onUploadProgress: (progressEvent) => {
        this.emit('progress', {
          file: this.file,
          size: this.fileSize,
          progress: progressEvent.loaded / progressEvent.total,
        });
      },
    });

    if (!res || res instanceof Error) {
      this.onError(res);
      return;
    }

    const { url } = res;
    this.emit('finish', {
      file_url: url,
      file: this.file,
      size: this.fileSize,
    });
  }

  upload() {
    this.generateTask();
    this.runTask();
  }

  generateTask() {
    for (let index = 0; index < this.chunkNum; index++) {
      const chunkSize = this.chunkSize;

      const startPos = index * chunkSize;
      const endPos = Math.min((index + 1) * chunkSize, this.fileSize);
      const currentUploadChunk = this.file.slice(startPos, endPos);
      this.taskList.push({
        // upload part时 index 必须从 1 开始
        index: index + 1,
        chunk: currentUploadChunk,
        startPos,
        endPos,
        status: TaskStatus.pending,
        loaded: 0,
        errorCount: 0,
      });
    }
    this.runningTask = [...this.taskList];

    this.emit('generateTask', {
      file: this.file,
      size: this.fileSize,
      taskList: this.taskList,
      upload_id: this.upload_id,
      file_key: this.file_key,
    });
    return this.taskList;
  }

  runTask() {
    this.getPendingTask().forEach(async (task) => {
      task.status = TaskStatus.uploading;
      // 如果是失败重传,如要重置已加载的文件大小
      task.loaded = 0;
      this.runningCount += 1;
      const res = await uploadPart(this.upload_id, this.file_key, task.index, task.chunk, {
        onUploadProgress: (progressEvent: ProgressEvent) => {
          task.loaded = progressEvent.loaded;
          this.emitProgress();
        },
        cancelToken: new Axios.CancelToken(function executor(cancel) {
          task.cancel = cancel;
        }),
      });
      this.runningCount -= 1;

      if (this.isAbort) {
        return;
      }
      if (res instanceof Error) {
        console.error(res);
        task.status = TaskStatus.pending;
        task.errorCount += 1;
        if (task.errorCount >= 2) {
          this.abort();
          this.onError(new Error('Maximum task run count exceeded'));
          return;
        }
        this.runTask();
        return;
      }

      task.status = TaskStatus.done;

      this.hasBeenSentCount += 1;
      task.loaded = task.chunk.size;
      this.emitProgress();
      this.completed_parts.push(res.data);

      if (this.taskList.filter((task) => task.status === TaskStatus.done).length === this.chunkNum) {
        this.finish();
        return;
      }

      this.runTask();
    });
  }

  async finish() {
    const res = await completeMultipartUpload(this.upload_id, this.file_key, this.completed_parts);

    if (res instanceof Error) {
      this.onError(res);
      return;
    }
    const { data: { file_url } = {}} = res;

    this.emit('finish', {
      file_url,
      file: this.file,
      size: this.fileSize,
      upload_id: this.upload_id,
      file_key: this.file_key,
    });
  }

  emitProgress() {
    this.emit('progress', {
      file: this.file,
      size: this.fileSize,
      taskList: this.taskList,
      upload_id: this.upload_id,
      file_key: this.file_key,
      hasBeenSentCount: this.hasBeenSentCount,
      hasBeenSentSize: this.getHasBeenSentSize(),
      progress: this.getProgress(),
    });
  }

  stat() {
    if (this.quiet) {
      return;
    }
  }

  getHasBeenSentSize() {
    return this.taskList
      .filter((task) => task.status === TaskStatus.done || task.status === TaskStatus.uploading)
      .reduce((acc, cur) => {
        return acc + cur.loaded;
      }, 0);
  }

  getProgress() {
    return this.getHasBeenSentSize() / this.fileSize;
  }

  // 还未消费的分片, 包括上传失败还有未进行过上传的分片
  getPendingTask() {
    const restConcurrency = Math.max(0, this.windowSize - this.runningCount);
    if (restConcurrency === 0) {
      return [];
    }

    const taskList = [];
    // 使用 for loop 减少循环次数
    for (const task of this.taskList) {
      if (task.status === TaskStatus.pending) {
        taskList.push(task);
      }
      if (this.taskList.length === restConcurrency) {
        break;
      }
    }
    return taskList;
  }

  // 目前只能识别到 400 异常, 而且有的异常只有在 complete 的时候才会返回
  onError(error: Error) {
    console.error(error);
    this.emit('error', error);
  }

  async abort() {
    if (!this.shouldMultipartUpload) {
      return;
    }
    this.isAbort = true;
    const res = await abortMultipartUpload(this.upload_id, this.file_key);

    this.taskList.filter((task) => task.status === TaskStatus.uploading).forEach((task) => task?.cancel?.('abort'));
    this.emit('abort', {
      file: this.file,
      size: this.fileSize,
      upload_id: this.upload_id,
      file_key: this.file_key,
      res,
    });
  }
}


export function useUpload(file: File | undefined | null, useInAgent = false) {
  const [error, setError] = useState<Error | undefined>(undefined);
  const [finish, setFinish] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileUrl, setFileUrl] = useState('');
  let uploadTask: UploadManager | null = null;
  const abort = useCallback(() => {
    uploadTask && uploadTask?.abort?.();
  }, [file, uploadTask]);

  useEffect(() => {
    if (!(file instanceof File)) {
      setError(new Error(`Params file is not an valid File Object: ${file}`));
      setProgress(0);
      setFinish(false);
      return;
    }

    // 如果会话已经断开了
    if (useInAgent && agentStore.agentEndedReason) {
      setError(new Error('session end'));
      return;
    }

    uploadTask = new UploadManager(file, {
      multipartUploadBlockSize: agentStore.multipartUploadBlockSize,
      multipartUploadConcurrency: agentStore.multipartUploadConcurrency,
    });

    uploadTask.on('init', () => {
      setProgress(0);
      setFinish(false);
      useInAgent && agentStore.UPDATE_FILE_UPLOADING_COUNT(UploadFileStatus.UPLOADING);
    });
    uploadTask.on('progress', (e) => {
      setProgress(e.progress);
    });
    uploadTask.on('finish', (e) => {
      setFileUrl(e.file_url);
      setFinish(true);
      setProgress(1);
      useInAgent && agentStore.UPDATE_FILE_UPLOADING_COUNT(UploadFileStatus.UPLOADED);
    });
    uploadTask.on('error', (e) => {
      setError(e);
      setFinish(false);
      setProgress(0);
      useInAgent && agentStore.UPDATE_FILE_UPLOADING_COUNT(UploadFileStatus.UPLOADED);
    });

    return () => {
      uploadTask?.abort();
      uploadTask?.removeAllListeners();
    };
  }, [file]);

  return {
    file,
    error,
    finish,
    progress,
    fileUrl,
    setError,
    setFinish,
    setProgress,
    setFileUrl,
    abort,
  };
}

